import { CompartmentCard } from "@/src/components/cards";
import { closeDrawer } from "@/src/components/dialogs";
import { FetchableList } from "@/src/components/fetchableLists";
import { FamilyMember, Patient } from "@/src/data/allTypes";
import apiFetch from "@/src/utils/apiFetch";
import { getItemId, getServiceId, getUserId, uid } from "@/src/utils/utils";
import { useState } from "react";
import {
  FamilyMemberCard,
  openFamilyMemberAntecedentsDrawer,
} from "./familyMemberCard";
import { openFamilyRelationshipPicker } from "./familyRelationshipPicker";
import { openPatientPicker } from "./patientPicker";
import { Button } from "@/src/components/buttons";
import StaggeredAnimation from "@/src/components/staggeredAnimationComponent";
import { getStandardConfidentiality } from "./confidentialitiesHook";
import moment from "moment";

export function FamilyAntecedentsCompartment({
  originalPatient,
  onAntecedentsChange,
}: {
  originalPatient: Patient;
  onAntecedentsChange: (newValue: Patient) => void;
}) {
  const [reload, setReload] = useState(0);

  return (
    <CompartmentCard title={"Antécédents Familiaux"}>
      <div className="c1 gap15">
        <FamilyMembersAntecedentsList
          key={`familyAntecedentsList-${originalPatient?.id}-${reload}`}
          originalPatient={originalPatient}
          onAntecedentsChange={onAntecedentsChange}
        ></FamilyMembersAntecedentsList>
        <Button
          type="tertiary"
          onClick={async () => {
            await openFamilyRelationshipPicker({
              current: null,
              onSelect: async (relationship) => {
                let patient: Patient = {
                  creator: getUserId(),
                  firstName: "",
                  lastName: "",
                  birth: moment("1900-01-01").format("YYYY-MM-DD"),
                  confidentiality: getStandardConfidentiality(originalPatient),
                  isFamilyMember: true,
                  services: [],
                  familyMembers: [],
                };

                patient = await apiFetch(
                  "patients",
                  "addFamilyMemberAsPatient",
                  {
                    patient,
                  },
                  "Erre ur lors de la création du membre de la famille"
                );

                const familyMember: FamilyMember = {
                  relationship,
                  patient,
                  creator: getUserId(),
                };
                patient = await apiFetch(
                  "familyMembers",
                  "addFamilyMemberToPatient",
                  {
                    patientId: getItemId(originalPatient),
                    familyMember,
                  },
                  "Erreur lors de l'ajout du membre de la famille"
                );
                closeDrawer("familyRelationshipDrawer");
                onAntecedentsChange(patient);
                setReload((prev) => prev + 1);

                openFamilyMemberAntecedentsDrawer({
                  familyMember,
                  antecedents: null,
                  onAntecedentsChange: (newValue) => {
                    onAntecedentsChange(patient);
                    setReload((prev) => prev + 1);
                  },
                });
              },
            });
          }}
        >
          Ajouter un membre
        </Button>
      </div>
    </CompartmentCard>
  );
}

export function FamilyMembersAntecedentsList({
  originalPatient,
  onAntecedentsChange,
  emptyChild,
}: {
  originalPatient: Patient;
  onAntecedentsChange: (newValue: Patient) => void;
  emptyChild?: React.ReactNode;
}) {
  const [reload, setReload] = useState(0);
  return (
    <FetchableList
      queryKey={["FamilyAntecedentsCompartment", originalPatient.id, reload]}
      queryFn={() =>
        apiFetch(
          "familyMembers",
          "loadFamilyMembers",
          {
            patientId: getItemId(originalPatient),
          },
          "Erreur lors du chargement des antécédents familiaux"
        )
      }
      emptyChild={
        emptyChild || (
          <div
            className="center tcenter p20 gray"
            style={{ marginTop: "10px" }}
          >
            Aucun membre de la famille trouvé
          </div>
        )
      }
    >
      {(data) => {
        return (
          <div className="c1 gap15">
            {data?.map((familyMember, index) => {
              if (!familyMember) return null;
              return (
                <StaggeredAnimation key={uid()} index={index}>
                  <FamilyMemberCard
                    familyMember={familyMember}
                    originalPatient={originalPatient}
                    onAntecedentsChange={(newValue) => {
                      onAntecedentsChange(originalPatient);
                    }}
                    onDelete={async () => {
                      setReload((prev) => prev + 1);
                    }}
                  ></FamilyMemberCard>
                </StaggeredAnimation>
              );
            })}
          </div>
        );
      }}
    </FetchableList>
  );
}
